import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import {
  calculateCommitmentGoalAchieved,
  calculateActualPurchasePace,
  getRemainingVolume,
  numberFormatter,
  numberFormatterInK,
} from "../../../../utils/formatter";
import DangerArrowUpSm from "../../../../assets/up-arrow-sm-danger.svg";
import WarnArrowUpSm from "../../../../assets/up-arrow-sm-warn.svg";
import SafeArrowDownSm from "../../../../assets/down-arrow-sm-safe.svg";
import EditIconDark from "../../../../assets/EditIconDark.svg";
import { Divider, Tooltip, Whisper } from "rsuite";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";
import { useEffect, useState } from "react";
import moment from "moment";
import {
  calculateNetworkingDaysUntilEndOfMonth,
  getNetworkingDaysInMonth,
  getNetworkingDaysUpToToday,
} from "../../../../utils/helpers/mapModulesToIcon";
import { useUpdateVolumeComplianceActualVolume } from "../../../../react-query/hooks/HCompliance";

const SHORT_MONTH_TO_FULL: Record<string, string> = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December",
};

// month is stored as a short "Mon YY" string (e.g. "Oct 26"), possibly wrapped
// in an array. Extract just the month portion and expand it to its full name
// (e.g. "October") for the empty-state message below.
const getFullMonthLabel = (monthValue: string[] | string | null | undefined) => {
  const raw = Array.isArray(monthValue) ? monthValue[0] : monthValue;
  if (!raw) return "";
  const shortMonth = raw.trim().slice(0, 3);
  return SHORT_MONTH_TO_FULL[shortMonth] ?? raw;
};

const ActualVsCommitedTab = ({ data, latestMonth, month, commodity, district }: any) => {
  const isCopper = commodity === "Copper Wire";
  // A single specific district must be selected to edit. When "All" districts
  // are selected (empty array) or multiple districts are chosen, editing is
  // disabled and the Actual Volume is shown directly from the API response.
  const isSingleDistrict = Array.isArray(district)
    ? district.length === 1
    : Boolean(district);
  const isEditable = isCopper && isSingleDistrict;
  const [tableData, setTableData] = useState<any[]>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const { mutate: updateActualVolume } = useUpdateVolumeComplianceActualVolume();

  useEffect(() => {
    if (data?.result) {
      // Hide rows that have neither a commitment nor any actual volume
      const filtered = data.result.filter(
        (row: any) =>
          row?.vendor === "TOTAL" ||
          Number(row?.Total_Commited_Volume) > 0 ||
          Number(row?.Total_Actual_Volume) > 0,
      );
      const hasSupplierRows = filtered.some(
        (row: any) => row?.vendor !== "TOTAL",
      );
      setTableData(hasSupplierRows ? [...filtered] : []);
    }
  }, [data]);

  const recalculateRow = (row: any) => {
    const monthValue = Array.isArray(month) ? month[0] : month;
    const momentObj = moment.utc(monthValue, "MMM-YY");
    const monthNumber = momentObj.month() + 1;
    const refreshedDate = data?.refreshed_date;

    const remaining_vol_tls = getRemainingVolume(row);
    row["remaining_vol_tls"] = remaining_vol_tls;

    const networkingDaysInMonth = getNetworkingDaysInMonth(moment.utc().year(), monthNumber);
    row["comm_pur_pac"] = ((row?.Total_Commited_Volume / networkingDaysInMonth) * 10) / 10;

    if (monthNumber !== moment.utc().month() + 1) {
      row["acc_pur_pac"] = ((row?.Total_Actual_Volume / networkingDaysInMonth) * 10) / 10;
      row["req_pur_pac"] = "-";
    } else {
      const dateTobeCalculatedFor = moment.utc(refreshedDate, "D");
      const formattedDay = dateTobeCalculatedFor.format("D");
      const workingDay = getNetworkingDaysUpToToday(parseInt(formattedDay, 10));
      row["acc_pur_pac"] = workingDay !== 0
        ? ((row?.Total_Actual_Volume / workingDay) * 10) / 10
        : 0.0;
      row["req_pur_pac"] =
        Math.round(
          (remaining_vol_tls / calculateNetworkingDaysUntilEndOfMonth(refreshedDate)) * 10
        ) / 10;
    }
    return row;
  };

  const handleActualVolumeChange = (rowIndex: number, newValue: string) => {
    const parsed = parseFloat(newValue);
    if (isNaN(parsed) && newValue !== "") return;

    const updatedData = [...tableData];
    const row = { ...updatedData[rowIndex] };
    row["Total_Actual_Volume"] = newValue === "" ? 0 : parsed;

    const recalculated = recalculateRow(row);
    updatedData[rowIndex] = recalculated;

    // Recalculate TOTAL row
    const totalIndex = updatedData.findIndex((r: any) => r.vendor === "TOTAL");
    if (totalIndex !== -1) {
      let totalActualVolume = 0;
      updatedData.forEach((item: any, idx: number) => {
        if (item.vendor !== "TOTAL") {
          totalActualVolume += item.Total_Actual_Volume || 0;
        }
      });
      const totalRow = { ...updatedData[totalIndex] };
      totalRow["Total_Actual_Volume"] = totalActualVolume;
      updatedData[totalIndex] = recalculateRow(totalRow);
    }

    setTableData(updatedData);
  };

  const handleSaveActualVolume = (rowData: any) => {
    const monthValue = Array.isArray(month) ? month[0] : month;
    const formattedMonth = monthValue
      ? moment.utc(monthValue, "MMM-YY").format("YYYY-MM-01")
      : null;
    const districtValue = Array.isArray(district) ? district[0] : district;

    const payload = {
      updates: [
        {
          commodity: commodity,
          actual_volume: rowData?.Total_Actual_Volume,
          district: districtValue,
          vendor: rowData?.vendor,
          month: formattedMonth,
        },
      ],
    };

    updateActualVolume(payload);
    setEditingRowIndex(null);
  };

  const getCommitmentGoalAchievedValue = (value: number) => {
    switch (true) {
      case value < 70:
        return (
          <div className="flex items-center">
            <div className="text-alert">{value}%</div>
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={DangerArrowUpSm}
                alt="danger"
              />
            </div>
          </div>
        );
      case value >= 70 && value < 100:
        return (
          <div className="flex items-center">
            <div className="text-warning">{value}%</div>
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={WarnArrowUpSm}
                alt="warn"
              />
            </div>
          </div>
        );
      case value >= 100:
        return (
          <div className="flex items-center">
            <div className="text-deloitteGreen">{value}%</div>
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={SafeArrowDownSm}
                alt="safe"
              />
            </div>
          </div>
        );

      default:
        return <>{value}</>;
    }
  };
  const getCommitmentActualPurchasePace = (value: number) => {
    switch (true) {
      case value < -10:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={DangerArrowUpSm}
                alt="danger"
              />
            </div>
          </div>
        );
      case value >= -10 && value <= 0:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={WarnArrowUpSm}
                alt="warn"
              />
            </div>
          </div>
        );
      case value > 0:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={SafeArrowDownSm}
                alt="safe"
              />
            </div>
          </div>
        );

      default:
        return <>{value}</>;
    }
  };

  const ActualVolumeTemplate = (rowdata: any, options: any) => {
    if (isEditable && rowdata?.vendor !== "TOTAL") {
      const isEditing = editingRowIndex === options.rowIndex;
      const displayValue = rowdata["Total_Actual_Volume"]
        ? isCopper
          ? Math.round(rowdata["Total_Actual_Volume"]).toLocaleString("en-US")
          : Number(rowdata["Total_Actual_Volume"]).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : "-";

      if (!isEditing) {
        return (
          <div
            className="flex flex-row justify-between items-center cursor-pointer"
            onClick={() => setEditingRowIndex(options.rowIndex)}
          >
            <div>{displayValue}</div>
            <img
              src={EditIconDark}
              alt="Edit"
              className="ml-[0.5vw] max-w-[0.625vw] min-w-[0.625vw] cursor-pointer"
            />
          </div>
        );
      }

      return (
        <div className="flex flex-row items-center gap-2">
          <input
            type="number"
            autoFocus
            className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            value={rowdata["Total_Actual_Volume"] ? rowdata["Total_Actual_Volume"] : ""}
            onChange={(e) => handleActualVolumeChange(options.rowIndex, e.target.value)}
          />
          <button
            type="button"
            className="shrink-0 rounded bg-deloitteGreen px-3 py-1 text-sm font-semibold text-white hover:opacity-90"
            onClick={() => handleSaveActualVolume(rowdata)}
          >
            Save
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-row justify-between">
        <div>
          {rowdata["Total_Actual_Volume"]
            ? isCopper
              ? Math.round(rowdata["Total_Actual_Volume"]).toLocaleString("en-US")
              : Number(rowdata["Total_Actual_Volume"]).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
            : "-"}
        </div>
      </div>
    );
  };

  const ActualPurchasePace = (rowData: any) => {
    const hasValue =
      rowData["acc_pur_pac"] != 0.0 && Boolean(rowData["acc_pur_pac"]);
    // When there is no commitment (commitment pace is 0/empty) but there IS
    // actual volume, the supplier is ahead by definition, so show a green arrow.
    const hasCommitment = Number(rowData["comm_pur_pac"]) > 0;
    return (
      <div className="flex flex-row justify-between">
        <div>{hasValue ? (isCopper ? Math.round(rowData["acc_pur_pac"]).toLocaleString("en-US") : Number(rowData["acc_pur_pac"]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : "-"}</div>
        {hasValue && (
          <div className="flex flex-nowrap gap-1">
            {hasCommitment
              ? getCommitmentActualPurchasePace(
                  calculateActualPurchasePace(
                    parseFloat(rowData["acc_pur_pac"]),
                    parseFloat(rowData["comm_pur_pac"]),
                  ),
                )
              : getCommitmentActualPurchasePace(1)}
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="bg-[#F1F1F1] grow shrink p-[2%] pr-[5%] flex flex-col flex-nowrap justify-start items-start gap-6 h-full w-full">
      {!isCopper && (
      <div className="flex flex-row gap-0 justify-between items-center basis-[10%] shrink-0 w-full h-full bg-white p-pd13 shadow-module-card border border-[#bebebe] rounded-md">
        <div className="flex flex-col justify-center p-[1%]">
          <div className="font-bold text-[1.6vh]">
            PPV Opportunity Cost
            <span>
              <Whisper
                placement="top"
                speaker={
                  <Tooltip placement="top">
                    Lost PPV due to allocating extra volume to non-lowest cost
                    suppliers
                  </Tooltip>
                }
              >
                {" "}
                <img
                  src={ProductInfoIcon}
                  alt="info"
                  className="w-[1.75vh] cursor-pointer inline"
                ></img>
              </Whisper>
            </span>
          </div>
          <div className="text-[1.4vh]  text-[#858585]">
            Based on purchasing over commitments from high-cost suppliers
          </div>
        </div>
        <div className="text-[4.8vh] pr-2">
          {numberFormatter(data?.overspend, 1, "$")}
        </div>
      </div>
      )}
      <div className="flex flex-row gap-4 justify-between items-center basis-[5%] shrink-0 w-full bg-white p-[1%] shadow-module-card border border-[#bebebe] rounded-md">
        <div className="font-bold text-[1.8vh]">
          Committed vs Actual Volume{" "}
          <span className="text-[1.6vh] text-[#858585]">
            {month === null || (month && month?.includes(latestMonth))
              ? "(Month To Date)"
              : ""}
          </span>
        </div>
        <div className="flex self-end justify-end items-center flex-nowrap gap-4">
          <div className="flex gap-1 items-center">
            <span className="font-semibold text-[1.5vh]">
              Commitment Goal Achieved
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="bg-deloitteGreen w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.25vh]">
              At or above pace
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="bg-amber w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.25vh]">
              {"> 0-10% below pace"}
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="bg-alert w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.25vh]">
              {"10% or more below pace"}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{ flex: "0.8" }}
        className="basis-[80%] w-full justify-start items-center"
      >
        <DataTable
          value={tableData}
          emptyMessage={`No available data for ${getFullMonthLabel(month) || "the selected month"}`}
          scrollable
          scrollHeight="60vh"
        >
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "100px",
            }}
            field="vendor"
            header="Supplier"
          ></Column>
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            field="Total_Commited_Volume"
            header={isCopper ? "Commitment (Lbs)" : "Commitment (TLs)"}
            body={(rowData) => {
              if (rowData.Total_Commited_Volume)
                return <div>{isCopper ? Math.round(rowData.Total_Commited_Volume).toLocaleString("en-US") : Number(rowData.Total_Commited_Volume).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</div>;
              else return <div>-</div>;
            }}
          ></Column>

          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            field="Total_Actual_Volume"
            header={isCopper ? "Actual Volume (Lbs)" : "Actual Volume (TLs)"}
            body={ActualVolumeTemplate}
          />
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            field="remaining_vol_tls"
            body={(rowData) => {
              if (rowData.remaining_vol_tls === 0.0) return <div>-</div>;
              else
                return (
                  <div>
                    {rowData?.remaining_vol_tls
                      ? isCopper
                        ? Math.round(rowData?.remaining_vol_tls).toLocaleString("en-US")
                        : Number(rowData?.remaining_vol_tls).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                      : "-"}
                  </div>
                );
            }}
            header={isCopper ? "Remaining Volume (Lbs)" : "Remaining Volume (TLs)"}
          />
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            field="comm_pur_pac"
            body={(rowData) => {
              if (rowData.comm_pur_pac == 0.0) return <div>-</div>;
              else
                return (
                  <div>
                    {rowData?.comm_pur_pac
                      ? isCopper
                        ? Math.round(rowData?.comm_pur_pac).toLocaleString("en-US")
                        : Number(rowData?.comm_pur_pac).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : "-"}
                  </div>
                );
            }}
            header={isCopper ? "Commitment Purchase Pace (Lbs/day)" : "Commitment Purchase Pace (TL/day)"}
          />
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            body={ActualPurchasePace}
            field="acc_pur_pac"
            header={isCopper ? "Actual Purchase Pace (Lbs/day)" : "Actual Purchase Pace (TL/day)"}
          />
          <Column
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            style={{
              width: "150px",
            }}
            field="req_pur_pac"
            body={(rowData) => {
              if (
                rowData.req_pur_pac == "0" ||
                rowData.req_pur_pac === "0.0" ||
                rowData.req_pur_pac === "0.00"
              )
                return <div>-</div>;
              else {
                const val = parseFloat(String(rowData?.req_pur_pac).replace(/,/g, ""));
                return <div>{isNaN(val) ? rowData?.req_pur_pac : (isCopper ? Math.round(val).toLocaleString("en-US") : val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}</div>;
              }
            }}
            header={isCopper ? "Required Purchase Pace (Lbs/day)" : "Required Purchase Pace (TL/day)"}
          />
          {!isCopper && (
          <Column
            style={{
              width: "180px",
            }}
            bodyClassName={(data: any) => {
              if (data?.vendor === "TOTAL") {
                return "total-footer-datatable";
              }
              return "";
            }}
            field="Overspend"
            header={
              <Whisper
                placement="top"
                className="inline"
                speaker={
                  <Tooltip style={{ maxWidth: "15vw" }} placement="top">
                    Lost PPV due to allocating extra volume to non-lowest cost
                    suppliers
                  </Tooltip>
                }
              >
                PPV Opportunity Cost{" "}
                <img
                  src={ProductInfoIcon}
                  alt="info"
                  className="w-[1.75vh] cursor-pointer inline"
                ></img>
              </Whisper>
            }
            body={(rowdata) => {
              return (
                <span>
                  {rowdata?.Overspend
                    ? numberFormatterInK(rowdata?.Overspend, 1, "$")
                    : "-"}
                </span>
              );
            }}
          ></Column>
          )}
        </DataTable>
      </div>
    </div>
  );
};

export default ActualVsCommitedTab;
